import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";

interface GradeInputProps {
  value: number;
  onChange: (value: number) => void;
  className?: string;
  onEnterPress?: () => void;
  inputRef?: (element: HTMLInputElement | null) => void;
  disabled?: boolean;
}

const GradeInput: React.FC<GradeInputProps> = ({ 
  value, 
  onChange, 
  className = "", 
  onEnterPress,
  inputRef,
  disabled = false
}) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState<string>(value?.toString() || "");
  const [error, setError] = useState<string>("");

  useEffect(() => {
    const formattedValue = Number.isInteger(value) ? value.toString() : value.toFixed(2);
    setInputValue(formattedValue);
  }, [value]);

  const validateGrade = (value: number): boolean => {
    // Allow 0, whole numbers from 2-6 and decimal values with .25, .5, and .75 increments
    if (value === 0) return true;
    if (value < 2 || value > 6) return false;
    
    // Check if it's a whole number
    if (Number.isInteger(value)) return true;
    
    // Check if it's a valid decimal (only .25, .5, and .75 are allowed)
    const decimalPart = value - Math.floor(value);
    return decimalPart === 0.25 || decimalPart === 0.5 || decimalPart === 0.75;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const newValue = e.target.value;
    setInputValue(newValue);
    
    if (newValue === "") {
      setError("");
      return;
    }

    const numValue = parseFloat(newValue);
    if (isNaN(numValue)) {
      setError(t("Please enter a valid number"));
      return;
    }

    if (!validateGrade(numValue)) {
      setError(t("Grade must be 0, between 2 and 6, or decimal values ending in .25, .5, or .75"));
      return;
    }

    setError("");
    onChange(numValue);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !error && onEnterPress && !disabled) {
      e.preventDefault();
      onEnterPress();
    }
  };

  const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
    e.currentTarget.blur();
  };

  const baseClassName = "bg-gray-200 border border-gray-400 rounded-md p-2 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white";
  const errorClassName = error ? "border-red-500 focus:ring-red-500" : "";
  const disabledClassName = disabled ? "opacity-60 cursor-not-allowed" : "";
  const finalClassName = `${baseClassName} ${errorClassName} ${disabledClassName} ${className}`;

  return (
    <div className="flex flex-col">
      <input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={inputValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onWheel={handleWheel}
        onFocus={(e) => e.target.select()}
        className={finalClassName}
        placeholder={t("Enter grade")}
        disabled={disabled}
      />
      {error && <span className="text-red-500 text-sm mt-1">{error}</span>}
    </div>
  );
};

export default GradeInput; 